import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../router/all_routes";
import {
  StatusBadge,
  WarningBanner,
  DepartmentClearanceCard,
  DeptClearanceDetailModal,
  useConfirm,
} from "./shared/components";
import { ResignationFormSections } from "./shared/ResignationFormSections";
import { NoDuesFormSections } from "./shared/NoDuesFormSections";
import {
  getResignationDetails,
  getNoDuesByRequest,
  getNoDuesFormDetails,
  getNoDuesDispatchList,
  getNoDuesDeptDetail,
  getNoDuesSettlementStatus,
  downloadNoDuesSettlementReport,
  submitApprovalAction,
  getEmployeeDetails,
  generateAcceptanceLetter,
  generateServiceCertificate,
  generateRelievingOrder,
  releaseFinalDocs,
  officeOrderWingLabel,
  generateNoDuesCertificate,
  NoDuesCase,
  NoDuesDispatchRow,
  NoDuesSettlementStatus,
  EmployeeDetails,
  VigilanceCaseRecord,
  formatDate,
  formatDateLong,
} from "./services/eSeparation.service";

const ROLE_LABELS: Record<string, string> = {
  EMPLOYEE: "Employee",
  DEPUTY: "Deputy",
  CO: "CO",
  HOD: "HOD",
  HR: "HR",
  CA: "CA",
  MD: "MD",
  ADMIN: "Admin",
};

const ACTION_META: Record<string, { label: string; color: string; bg: string }> = {
  SUBMIT: { label: "Submitted", color: "#185FA5", bg: "#e8f2fc" },
  RESUBMIT: { label: "Resubmitted", color: "#185FA5", bg: "#e8f2fc" },
  APPROVE: { label: "Approved", color: "#049123", bg: "#e6f4ea" },
  REJECT: { label: "Rejected", color: "#C0282C", bg: "#fce8e6" },
  SEND_BACK: { label: "Sent Back", color: "#BA7517", bg: "#fef7e0" },
  CLARIFY: { label: "Clarification", color: "#BA7517", bg: "#fef7e0" },
  AUTO_FORWARD: { label: "Auto-Forwarded", color: "#78746D", bg: "#f1f3f4" },
};

const actionMeta = (action: string) =>
  ACTION_META[String(action || "").toUpperCase()] || {
    label: action || "—",
    color: "#78746D",
    bg: "#f1f3f4",
  };

const fmt = (v: any): string => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}/.test(v)) {
    return formatDate(v);
  }
  return String(v);
};

const HistoryRow: React.FC<{ entry: any }> = ({ entry }) => {
  const meta = actionMeta(entry.action);
  const role =
    ROLE_LABELS[String(entry.role || "").toUpperCase()] || entry.role || "—";
  const name = entry.approver_name || "—";
  const empNo = entry.approver_emp_id || entry.approver_emp_no || "—";
  const when = entry.acted_at ? String(entry.acted_at).slice(0, 10) : "";
  return (
    <div className="p-3 mb-2 rounded-3 bg-light border d-flex align-items-start gap-3">
      <span
        className="d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0 mt-1"
        style={{
          width: 28,
          height: 28,
          background: meta.bg,
          color: meta.color,
          fontSize: 14,
        }}
      >
        <i
          className={`ti ${
            String(entry.action).toUpperCase().includes("APPROV")
              ? "ti-check"
              : String(entry.action).toUpperCase().includes("REJECT")
              ? "ti-x"
              : "ti-arrow-right"
          }`}
        />
      </span>
      <div className="flex-grow-1 overflow-hidden">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-1">
          <span className="fw-bold text-dark">{name}</span>
          <span
            className="badge rounded-pill"
            style={{ background: meta.bg, color: meta.color, fontSize: 11 }}
          >
            {meta.label}
          </span>
        </div>
        <div className="text-muted small">
          {role} • {empNo}
          {when ? ` • ${formatDate(when)}` : ""}
        </div>
        {entry.comments ? (
          <div
            className="p-2 px-3 rounded-2 bg-white text-dark small border mt-2"
            style={{ fontStyle: "italic" }}
          >
            "{entry.comments}"
          </div>
        ) : (
          <div className="text-muted small mt-1">No remarks recorded</div>
        )}
      </div>
    </div>
  );
};

const NoDuesHRReviewDetail: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const routes = all_routes;

  const state = (location.state as any) || {};
  const requestId: string = state.request_id || params.id || "";

  const [loading, setLoading] = useState(true);
  const [resignation, setResignation] = useState<any>(null);
  const [empMaster, setEmpMaster] = useState<EmployeeDetails | null>(null);
  const [caseData, setCaseData] = useState<
    (NoDuesCase & { designation?: string }) | null
  >(null);
  const [submitting, setSubmitting] = useState(false);

  // No-Dues state
  const [ndRequestId, setNdRequestId] = useState("");
  const [ndSubmitted, setNdSubmitted] = useState(false);
  // The employee's submitted No-Dues form payload (esep_no_dues.details) —
  // rendered via the shared NoDuesFormSections, identical to what the
  // employee saw and filled in on their own No-Dues form.
  const [ndForm, setNdForm] = useState<any>(null);
  const [noDuesStatus, setNoDuesStatus] = useState("");
  const [dispatchRows, setDispatchRows] = useState<NoDuesDispatchRow[]>([]);

  // Consolidated recovery-settlement snapshot
  const [settlement, setSettlement] = useState<NoDuesSettlementStatus | null>(null);
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [downloading, setDownloading] = useState<Record<string, boolean>>({});

  // Final release of the Relieving Order / Service Certificate (HR Assistant only).
  const [officeOrderNo, setOfficeOrderNo] = useState("");
  const [releasing, setReleasing] = useState(false);

  // Department clearance detail modal
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailDept, setDetailDept] = useState("");
  const [detailItems, setDetailItems] = useState<
    { item_name: string; comments?: string | null; amount_due: number }[]
  >([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const { confirm, confirmDialog } = useConfirm();

  const openDeptDetail = async (row: NoDuesDispatchRow) => {
    setDetailDept(row.department_name);
    setDetailItems([]);
    setDetailOpen(true);
    setDetailLoading(true);
    const res = await getNoDuesDeptDetail(row.id);
    setDetailItems(res.data?.checklist_items || []);
    setDetailLoading(false);
  };

  useEffect(() => {
    if (!requestId) {
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    (async () => {
      try {
        const resgRes = await getResignationDetails(requestId);
        const data = resgRes.data || null;
        const ndRes = await getNoDuesByRequest(requestId);
        if (!active) return;
        if (data) setResignation(data);
        const empNo = data?.emp_no || state.emp_no;
        if (empNo) {
          getEmployeeDetails(empNo).then((emp) => {
            if (active && emp.data) setEmpMaster(emp.data);
          });
        }
        if (ndRes.data) {
          setCaseData({
            ...ndRes.data,
            emp_name: data?.emp_name || ndRes.data.emp_name || "",
            designation: data?.designation || "—",
          });
        } else {
          toast.info("No-Dues records not initialized for this request.");
        }
      } catch {
        if (active) toast.error("Failed to load details.");
      }
      if (active) setLoading(false);
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId]);

  useEffect(() => {
    if (!requestId) return;
    let active = true;
    (async () => {
      const ndRes = await getNoDuesFormDetails(requestId);
      const noDuesRequestId = ndRes.data?.nodues_request_id || "";
      if (!active) return;
      setNdSubmitted(!!noDuesRequestId);
      setNdForm(ndRes.data?.details || null);

      const resolvedNdId = noDuesRequestId || requestId;
      setNdRequestId(resolvedNdId);

      const [rows, ndDetailRes, settlementRes] = await Promise.all([
        getNoDuesDispatchList(resolvedNdId),
        noDuesRequestId
          ? getResignationDetails(noDuesRequestId).catch(() => null)
          : Promise.resolve(null),
        noDuesRequestId
          ? getNoDuesSettlementStatus(noDuesRequestId).catch(() => null)
          : Promise.resolve(null),
      ]);
      if (!active) return;
      setDispatchRows(rows.data || []);
      setSettlement((settlementRes as any)?.data || null);

      const ndDetail = (ndDetailRes as any)?.data;
      setNoDuesStatus(String(ndDetail?.status ?? "").trim().toUpperCase());
    })();
    return () => {
      active = false;
    };
  }, [requestId]);

  const dispatchStatusLabel = (s: string) =>
    s === "CLEARED" ? "Cleared" : s === "REJECTED" ? "Dues Found" : "Pending";

  const handleDownloadReport = async () => {
    if (!ndRequestId) return;
    setDownloadingReport(true);
    const res = await downloadNoDuesSettlementReport(ndRequestId);
    setDownloadingReport(false);
    if (!res.success)
      toast.error(res.message || "Failed to download settlement report");
  };

  const handleRelease = async () => {
    if (!requestId) return;
    const no = officeOrderNo.trim();
    if (!no) return toast.warning("Please enter the office order number.");
    if (!/^[A-Za-z0-9][A-Za-z0-9\-/]{0,29}$/.test(no))
      return toast.warning(
        'Office order number may contain only letters, digits, "-" and "/" (max 30 characters).'
      );
    const wing = officeOrderWingLabel(resignation?.wing);
    const year = new Date().getFullYear();
    const ok = await confirm(
      `Release the Relieving Order and Service Certificate with Office Order No. ${wing}/HR- ${no} of ${year}? Once released the number cannot be changed.`,
      { title: "Final Release", confirmLabel: "Release", tone: "success" }
    );
    if (!ok) return;
    setReleasing(true);
    const res = await releaseFinalDocs(requestId, no);
    setReleasing(false);
    if (res.success) {
      toast.success("Relieving Order and Service Certificate released.");
      setResignation((prev: any) => ({
        ...prev,
        office_order_no: no,
        office_order_released_at: new Date().toISOString(),
        can_release_final_docs: false,
      }));
    } else {
      toast.error(res.message || "Failed to release. Please try again.");
    }
  };

  const handleDownloadDoc = async (
    docType: "acceptance_letter" | "service_certificate" | "relieving_letter" | "no_dues_certificate_doc",
    docName: string,
    available: boolean
  ) => {
    if (!requestId) return;
    if (!available) {
      toast.warning(`${docName} is not yet available.`);
      return;
    }
    setDownloading((p) => ({ ...p, [docType]: true }));
    let res;
    switch (docType) {
      case "acceptance_letter":
        res = await generateAcceptanceLetter(requestId);
        break;
      case "service_certificate":
        res = await generateServiceCertificate(requestId);
        break;
      case "relieving_letter":
        res = await generateRelievingOrder(requestId);
        break;
      case "no_dues_certificate_doc":
        res = await generateNoDuesCertificate(requestId);
        break;
    }
    setDownloading((p) => ({ ...p, [docType]: false }));
    if (res.success) toast.success(`${docName} downloaded successfully.`);
    else toast.error(res.message || `Failed to download ${docName}.`);
  };

  /* ── Final-review derivations ── */
  const resignStatus = String(resignation?.status || "").trim().toUpperCase();
  // V2: the parallel Competent Authority decision (esep_ca_approval) has no
  // row in `steps`/`history` shaped like V1's is_ca step at all — read it
  // directly. Also cover the case where the resignation has since fully
  // finalized (status flips from CA_APPROVED to APPROVED once all 3
  // conditions are met), which otherwise made this flip back to "not
  // approved" the moment finalization completed.
  const caApproval = resignation?.ca_approval || null;
  const caApproved = caApproval
    ? caApproval.status === "APPROVED"
    : resignStatus === "CA_APPROVED" || resignStatus === "APPROVED";
  const isFinalized = ["APPROVED", "REJECTED", "WITHDRAWN"].includes(resignStatus);
  const noDuesSettled = ndSubmitted && noDuesStatus === "APPROVED";
  const canApprove = caApproved && noDuesSettled;
  // Final release state (from find-one): everything approved + cleared, the
  // office order number once released, and whether THIS viewer (the HR Assistant)
  // may release.
  const finalReady = !!resignation?.final_docs_ready;
  const finalReleased = !!resignation?.office_order_no;
  const canRelease = !!resignation?.can_release_final_docs;

  const history: any[] = Array.isArray(resignation?.history)
    ? resignation.history
    : [];
  const steps: any[] = Array.isArray(resignation?.steps) ? resignation.steps : [];

  const caStep = steps.find((s: any) => s?.is_ca === true || s?.is_ca === "true");
  const caEmpId = caApproval
    ? String(caApproval.approver_emp_id ?? "").trim().padStart(8, "0")
    : String(caStep?.approver_emp_id ?? "").trim().padStart(8, "0");
  // Written by the same writeHistory() the step-based chain uses (processCaAction
  // calls it too), so filtering by approver_emp_id finds the CA's decision either way.
  const caHistory = history.filter(
    (h: any) =>
      String(h?.approver_emp_id ?? "").trim().padStart(8, "0") === caEmpId &&
      caEmpId !== "00000000"
  );
  const caRoleLabel = caApproval
    ? ROLE_LABELS[String(caApproval.ca_role || "").toUpperCase()] || caApproval.ca_role || "Competent Authority"
    : caStep
      ? ROLE_LABELS[String(caStep.role || "").toUpperCase()] || caStep.role
      : null;

  const empGroup = String(resignation?.emp_group ?? "").trim().toUpperCase();
  const isExecutive = empGroup === "A";
  const darRole = isExecutive ? "HOD" : "DY_HOD";
  const darRoleLabel = isExecutive ? "HOD" : "Dy. HOD";

  // Already part of the same find-one response fetched into `resignation` —
  // no separate call needed, same as HRVerificationDetail.tsx's vigilance_case.
  const vigilanceCase: VigilanceCaseRecord | null = resignation?.vigilance_case ?? null;
  const darEntries = history.filter(
    (h: any) =>
      String(h?.role ?? "").trim().toUpperCase() === darRole &&
      !!(h?.comments && String(h.comments).trim())
  );

  const ledger = useMemo(
    () =>
      history.filter(
        (h: any) =>
          caEmpId === "00000000" ||
          String(h?.approver_emp_id ?? "").trim().padStart(8, "0") !== caEmpId
      ),
    [history, caEmpId]
  );

  // Terminal step — a single one-time forward, no remarks and no reject
  // option. Finalizes the separation once CA has approved and No-Dues is
  // settled (canApprove).
  const handleForward = async () => {
    if (!requestId) return toast.warning("Resignation not loaded.");
    if (!canApprove)
      return toast.warning(
        !caApproved
          ? "The Competent Authority has not approved this resignation yet."
          : "The No-Dues settlement is not yet closed for this employee."
      );

    const ok = await confirm(
      "Complete final HR review and finalize this resignation?",
      { title: "Confirm Final Review", confirmLabel: "Forward", tone: "success" }
    );
    if (!ok) return;

    setSubmitting(true);
    const res = await submitApprovalAction({
      request_id: requestId,
      action: "Approve",
      comments: "",
    } as any);
    setSubmitting(false);

    if (res.success) {
      toast.success("Resignation has been finalized. The employee separation is now complete.");
      navigate(routes.Esepration.noDuesHrReview);
    } else {
      toast.error(res.message || "Failed to submit action. Please try again.");
    }
  };

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status" />
            <p className="text-muted mt-3 mb-0">Loading Clearance Data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!caseData) {
    return (
      <div className="page-wrapper">
        <div className="content pb-5">
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
            <div>
              <h4 className="fw-bold text-dark mb-0">HR Final Review</h4>
              <div className="text-muted small">{requestId}</div>
            </div>
            <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate(-1)}>
              <i className="ti ti-arrow-left me-1" />
              Exit
            </button>
          </div>
          <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
            <div className="card-body p-4">
              <WarningBanner
                type="warning"
                message="No-Dues records not available for this case."
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      {confirmDialog}
      <div className="content pb-5">
        {/* Breadcrumbs & Page Header */}
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
          <div>
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb mb-1 small">
                <li className="breadcrumb-item">
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(routes.Esepration.hub);
                    }}
                    className="text-decoration-none text-muted"
                  >
                    E-Separation
                  </a>
                </li>
                <li className="breadcrumb-item">
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(routes.Esepration.noDuesHrReview);
                    }}
                    className="text-decoration-none text-muted"
                  >
                    HR Final Review
                  </a>
                </li>
                <li className="breadcrumb-item active text-primary fw-medium" aria-current="page">
                  Review Details
                </li>
              </ol>
            </nav>
            <h4 className="fw-bold text-dark mb-0">HR Final Review</h4>
          </div>
          <div className="d-flex align-items-center gap-2">
            <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate(-1)}>
              <i className="ti ti-arrow-left me-1" />
              Exit
            </button>
          </div>
        </div>

        {/* Sub-header Banner */}
        <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
          <div className="card-body py-3 px-4">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
              <div className="d-flex align-items-center gap-3">
                <span
                  className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 10,
                    background: "#FDE8E0",
                    color: "#993C1D",
                  }}
                >
                  <i className="ti ti-user-check" style={{ fontSize: 22 }} />
                </span>
                <div>
                  <div
                    style={{
                      fontSize: "17px",
                      fontWeight: 700,
                      color: "#344054",
                      lineHeight: "1.3",
                    }}
                  >
                    {fmt(caseData.emp_name)}
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: 400,
                      color: "#667085",
                      lineHeight: "1.4",
                      marginTop: "2px",
                    }}
                  >
                    {requestId} • {fmt(caseData.emp_no)}
                  </div>
                </div>
              </div>
              <div>
                <StatusBadge status={resignation?.status || caseData.status} />
              </div>
            </div>
          </div>
        </div>

        {/* Resignation's own 7-section form, rendered identically to the
            employee's own view and to the HR Verification & Processing tab
            (read-only here — this is a final review stage, not an edit
            stage). */}
        <ResignationFormSections d={resignation || {}} mode="view" empMaster={empMaster} />

        {/* SECTION 08: APPROVAL HISTORY */}
        <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
          <div className="card-header bg-white border-bottom py-3 px-4 d-flex align-items-center justify-content-between">
            <div>
              <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                <span
                  className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                  style={{ width: 34, height: 34, fontSize: 15 }}
                >
                  08
                </span>
                Approval History
              </h5>
              <div className="text-muted small mt-1 ms-4 ps-2">
                Complete approval trail with approver name, employee no. and remarks.
              </div>
            </div>
          </div>
          <div className="card-body p-4">
            {ledger.length > 0 ? (
              ledger.map((h: any, i: number) => <HistoryRow key={i} entry={h} />)
            ) : (
              <WarningBanner type="info" message="No approval actions recorded yet." />
            )}
          </div>
        </div>

        {/* SECTION 09: D&AR REMARKS */}
        <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
          <div className="card-header bg-white border-bottom py-3 px-4 d-flex align-items-center justify-content-between">
            <div>
              <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                <span
                  className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                  style={{ width: 34, height: 34, fontSize: 15 }}
                >
                  09
                </span>
                D&AR Remarks
              </h5>
              <div className="text-muted small mt-1 ms-4 ps-2">
                {isExecutive ? "Executive" : "Non-Executive"} employee — recorded by {darRoleLabel}
              </div>
            </div>
          </div>
          <div className="card-body p-4">
            {darEntries.length > 0 ? (
              darEntries.map((h: any, i: number) => (
                <div
                  key={i}
                  className="p-3 mb-2 rounded-3 border"
                  style={{ background: "#FFF9F1", borderColor: "#EAD9BE" }}
                >
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <span className="fw-bold text-dark">{h.approver_name || "—"}</span>
                    <span
                      className="badge"
                      style={{ background: "#BA7517", color: "#fff" }}
                    >
                      {darRoleLabel}
                    </span>
                  </div>
                  <div className="text-muted small">
                    {h.approver_emp_id || "—"}
                    {h.acted_at ? ` • ${formatDate(String(h.acted_at).slice(0, 10))}` : ""}
                  </div>
                  <div className="p-2 px-3 rounded-2 bg-white text-dark small border mt-2 fst-italic">
                    "{h.comments}"
                  </div>
                </div>
              ))
            ) : (
              <WarningBanner
                type="info"
                message={`No D&AR remarks recorded by the ${darRoleLabel} yet.`}
              />
            )}
          </div>
        </div>

        {/* SECTION 10: VIGILANCE CLEARANCE */}
        <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
          <div className="card-header bg-white border-bottom py-3 px-4 d-flex align-items-center justify-content-between">
            <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
              <span
                className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                style={{ width: 34, height: 34, fontSize: 15 }}
              >
                10
              </span>
              Vigilance Clearance
            </h5>
            {vigilanceCase && <StatusBadge status={vigilanceCase.status} />}
          </div>
          <div className="card-body p-4">
            {!vigilanceCase ? (
              <WarningBanner type="info" message="This request has not been sent to Vigilance." />
            ) : (
              <div className="row g-3">
                <div className="col-md-4 col-sm-6">
                  <label className="form-label fw-semibold text-secondary small mb-1">Vigilance Officer</label>
                  <input type="text" className="form-control bg-light" value={vigilanceCase.officer_emp_id || "—"} readOnly />
                </div>
                {vigilanceCase.pending_case_ref && (
                  <div className="col-md-4 col-sm-6">
                    <label className="form-label fw-semibold text-secondary small mb-1">Pending Case Reference</label>
                    <input type="text" className="form-control bg-light" value={vigilanceCase.pending_case_ref} readOnly />
                  </div>
                )}
                {vigilanceCase.dispatched_at && (
                  <div className="col-md-4 col-sm-6">
                    <label className="form-label fw-semibold text-secondary small mb-1">Sent On</label>
                    <input type="text" className="form-control bg-light" value={formatDateLong(vigilanceCase.dispatched_at)} readOnly />
                  </div>
                )}
                {vigilanceCase.acted_at && (
                  <div className="col-md-4 col-sm-6">
                    <label className="form-label fw-semibold text-secondary small mb-1">Cleared / Actioned On</label>
                    <input type="text" className="form-control bg-light" value={formatDateLong(vigilanceCase.acted_at)} readOnly />
                  </div>
                )}
                {vigilanceCase.remarks && (
                  <div className="col-12">
                    <label className="form-label fw-semibold text-secondary small mb-1">Remarks</label>
                    <div className="p-2 px-3 rounded-2 bg-white text-dark small border fst-italic">
                      "{vigilanceCase.remarks}"
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* SECTION 11: COMPETENT AUTHORITY */}
        <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
          <div className="card-header bg-white border-bottom py-3 px-4 d-flex align-items-center justify-content-between">
            <div>
              <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                <span
                  className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                  style={{ width: 34, height: 34, fontSize: 15 }}
                >
                  11
                </span>
                Competent Authority
              </h5>
              <div className="text-muted small mt-1 ms-4 ps-2">
                {caRoleLabel
                  ? `${caRoleLabel} — ${caStep?.approver_name || caEmpId || "—"} (${caEmpId || "—"})`
                  : "Competent Authority assignment and decision."}
              </div>
            </div>
          </div>
          <div className="card-body p-4">
            {caHistory.length > 0 ? (
              caHistory.map((h: any, i: number) => <HistoryRow key={i} entry={h} />)
            ) : (
              <WarningBanner
                type="warning"
                message="Awaiting Competent Authority decision."
              />
            )}
          </div>
        </div>

        {/* SECTIONS 12-15: the employee's own submitted No-Dues form, rendered
            identically to how they filled it in. */}
        <NoDuesFormSections form={ndForm} startNumber={12} />

        {/* SECTION 16: NO-DUES & DEPARTMENT CLEARANCES */}
        <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
          <div className="card-header bg-white border-bottom py-3 px-4 d-flex align-items-center justify-content-between">
            <div>
              <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                <span
                  className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                  style={{ width: 34, height: 34, fontSize: 15 }}
                >
                  16
                </span>
                Department Clearances Status
              </h5>
              <div className="text-muted small mt-1 ms-4 ps-2">
                Live clearance status for each department this No-Dues was sent to.
              </div>
            </div>
          </div>
          <div className="card-body p-4">
            {!ndSubmitted ? (
              <WarningBanner
                type="info"
                message="The employee has not submitted their No-Dues form yet."
              />
            ) : (
              <>
                <div className="d-flex align-items-center gap-2 mb-4 p-3 rounded-3 bg-light border">
                  <span className="fw-semibold text-secondary small">No-Dues Request Status:</span>
                  <StatusBadge status={noDuesStatus || "PENDING_APPROVAL"} />
                </div>

                {dispatchRows.length > 0 ? (
                  <div className="row g-3">
                    {dispatchRows.map((row) => (
                      <div
                        className="col-md-6 col-xl-4"
                        key={row.id}
                        style={{ cursor: "pointer" }}
                        onClick={() => openDeptDetail(row)}
                      >
                        <DepartmentClearanceCard
                          deptName={row.department_name}
                          nodalOfficer={row.approver_emp_id || "—"}
                          status={dispatchStatusLabel(row.status)}
                          remarks={row.remarks || ""}
                          recoveryAmount={
                            row.recovery_amount ? Number(row.recovery_amount) : 0
                          }
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <WarningBanner
                    type="info"
                    message="This No-Dues has not been sent to any department yet."
                  />
                )}
              </>
            )}
          </div>
        </div>

        {/* SECTION 17: CONSOLIDATED RECOVERY SETTLEMENT */}
        {settlement?.settlement && (
          <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
            <div className="card-header bg-white border-bottom py-3 px-4 d-flex align-items-center justify-content-between">
              <div>
                <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                  <span
                    className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                    style={{ width: 34, height: 34, fontSize: 15 }}
                  >
                    17
                  </span>
                  Consolidated Recovery Settlement
                </h5>
                <div className="text-muted small mt-1 ms-4 ps-2">
                  Live status of dues sent to the settlement department.
                </div>
              </div>
            </div>
            <div className="card-body p-4">
              <div className="row g-3 mb-4">
                <div className="col-lg-3 col-md-4 col-sm-6">
                  <label className="form-label fw-semibold text-secondary small mb-1">Settlement Dept</label>
                  <input
                    type="text"
                    className="form-control bg-light"
                    value={settlement.settlement.department_name}
                    readOnly
                  />
                </div>

                <div className="col-lg-3 col-md-4 col-sm-6">
                  <label className="form-label fw-semibold text-secondary small mb-1">Status</label>
                  <div className="pt-1">
                    <span
                      className={`badge ${
                        settlement.settlement.status === "CLOSED" ? "bg-success" : "bg-warning text-dark"
                      }`}
                    >
                      {settlement.settlement.status === "CLOSED" ? "Closed" : "In Progress"}
                    </span>
                  </div>
                </div>

                <div className="col-lg-3 col-md-4 col-sm-6">
                  <label className="form-label fw-semibold text-secondary small mb-1">Total Due</label>
                  <input
                    type="text"
                    className="form-control bg-light fw-bold text-danger"
                    value={`₹ ${Number(settlement.settlement.total_due).toLocaleString("en-IN")}`}
                    readOnly
                  />
                </div>

                <div className="col-lg-3 col-md-4 col-sm-6">
                  <label className="form-label fw-semibold text-secondary small mb-1">Total Collected</label>
                  <input
                    type="text"
                    className="form-control bg-light fw-bold text-success"
                    value={`₹ ${Number(settlement.settlement.total_collected).toLocaleString("en-IN")}`}
                    readOnly
                  />
                </div>
              </div>

              <div className="border-top pt-3">
                <label className="form-label fw-semibold text-secondary small mb-2">
                  Recovery Items Breakdown
                </label>
                <div className="row g-2">
                  {settlement.items.map((it) => (
                    <div className="col-md-6" key={it.id}>
                      <div className="p-3 rounded-3 bg-light border d-flex justify-content-between align-items-center">
                        <div>
                          <div className="fw-bold text-dark small">{it.item_name}</div>
                          <div className="text-muted small">
                            {it.source_department_name} • ₹
                            {Number(it.amount_due).toLocaleString("en-IN")}
                          </div>
                        </div>
                        <span
                          className="badge"
                          style={{
                            background: it.collected ? "#E6F4EA" : "#FDECEC",
                            color: it.collected ? "#1B7A3D" : "#B3261E",
                            fontSize: 12,
                          }}
                        >
                          {it.collected
                            ? `Collected ₹${Number(it.collected_amount).toLocaleString(
                                "en-IN"
                              )}`
                            : it.acted_at
                            ? "Not Collected"
                            : "Pending"}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {settlement.settlement.status === "CLOSED" && (
                <div className="mt-4 pt-3 border-top">
                  <button
                    className="btn btn-outline-primary d-flex align-items-center justify-content-center gap-2 px-4 py-2"
                    style={{ borderRadius: 10 }}
                    onClick={handleDownloadReport}
                    disabled={downloadingReport}
                  >
                    {downloadingReport ? (
                      <span className="spinner-border spinner-border-sm" role="status" />
                    ) : (
                      <i className="ti ti-download" />
                    )}
                    <span>Download Settlement Report PDF</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* SECTION 17/18: FINAL HR ACTIONS */}
        <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
          <div className="card-header bg-white border-bottom py-3 px-4 d-flex align-items-center justify-content-between">
            <div>
              <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                <span
                  className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                  style={{ width: 34, height: 34, fontSize: 15 }}
                >
                  {settlement?.settlement ? "18" : "17"}
                </span>
                Final HR Actions
              </h5>
            </div>
          </div>
          <div className="card-body p-4">
            {isFinalized ? (
              <WarningBanner
                type={resignStatus === "APPROVED" ? "success" : "info"}
                message={
                  resignStatus === "APPROVED"
                    ? "This resignation has been finally approved. The separation is finalized."
                    : resignStatus === "WITHDRAWN"
                    ? "This resignation has been withdrawn."
                    : "This resignation has been rejected."
                }
              />
            ) : (
              <>
                {!caApproved && (
                  <WarningBanner
                    type="warning"
                    message="Awaiting Competent Authority approval. Final HR sign-off unlocks once the CA approves."
                  />
                )}
                {caApproved && !noDuesSettled && (
                  <WarningBanner
                    type="warning"
                    message="The No-Dues settlement is not yet closed. Final approval unlocks once it is settled in the HR Verification tab."
                  />
                )}

                {/* This is the terminal step — no remarks, no reject. A single
                    one-time forward finalizes the separation once CA has
                    approved and No-Dues is settled. */}
                <div className="d-flex align-items-center gap-3 pt-2">
                  <button
                    className="btn btn-success d-flex align-items-center justify-content-center gap-2 px-4 py-2"
                    style={{
                      borderRadius: 10,
                      height: 42,
                      opacity: !canApprove || submitting ? 0.5 : 1,
                    }}
                    onClick={handleForward}
                    disabled={!canApprove || submitting}
                  >
                    {submitting ? (
                      <span className="spinner-border spinner-border-sm" />
                    ) : (
                      <i className="ti ti-arrow-forward-up" />
                    )}
                    <span>Forward</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* SECTION 19: DOCUMENT GENERATION */}
        <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
          <div className="card-header bg-white border-bottom py-3 px-4">
            <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
              <span
                className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                style={{ width: 34, height: 34, fontSize: 15 }}
              >
                <i className="ti ti-file-certificate" />
              </span>
              Document Generation
            </h5>
          </div>
          <div className="card-body p-4">
            {/* Final release — the Relieving Order and Service Certificate are
                released only by the HR Assistant, who supplies the office order
                number printed on the Relieving Order. */}
            {finalReleased ? (
              <div
                className="d-flex align-items-start gap-2 rounded p-3 mb-3"
                style={{ background: "#E6F4EA", border: "1px solid #b7dfc2" }}
              >
                <i className="ti ti-circle-check mt-1" style={{ color: "#1B7A3D" }} />
                <div className="small" style={{ color: "#1B7A3D" }}>
                  <strong>Released</strong> — Office Order No.{" "}
                  <strong>
                    {officeOrderWingLabel(resignation?.wing)}/HR- {resignation?.office_order_no} of{" "}
                    {resignation?.office_order_released_at
                      ? new Date(resignation.office_order_released_at).getFullYear()
                      : new Date().getFullYear()}
                  </strong>
                  . The Relieving Order and Service Certificate are available below.
                </div>
              </div>
            ) : canRelease ? (
              <div className="rounded p-3 mb-3" style={{ background: "#FFF8EC", border: "1px solid #f0d9a8" }}>
                <div className="fw-semibold mb-1">Final release — Relieving Order &amp; Service Certificate</div>
                <div className="small text-muted mb-3">
                  All approvals, the Competent Authority and the No-Dues (including Establishment) are
                  complete. Enter the office order number to release both documents.
                </div>
                <label className="form-label fw-semibold text-secondary small mb-1">
                  Office Order Number <span className="text-danger">*</span>
                </label>
                <div className="d-flex flex-wrap align-items-center gap-2">
                  <span className="fw-semibold">
                    OFFICE ORDER No. {officeOrderWingLabel(resignation?.wing)}/HR-
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    style={{ maxWidth: 220 }}
                    placeholder="e.g. 00013161"
                    maxLength={30}
                    value={officeOrderNo}
                    onChange={(e) => setOfficeOrderNo(e.target.value)}
                  />
                  <span className="fw-semibold">of {new Date().getFullYear()}</span>
                  <button
                    type="button"
                    className="btn btn-success ms-md-2"
                    disabled={releasing || !officeOrderNo.trim()}
                    onClick={handleRelease}
                  >
                    {releasing ? "Releasing…" : "Release"}
                  </button>
                </div>
              </div>
            ) : finalReady ? (
              <WarningBanner
                type="info"
                message="Everything is approved and cleared. The Relieving Order and Service Certificate will be available once the HR Assistant gives the final release."
              />
            ) : (
              <WarningBanner
                type="info"
                message="The Relieving Order and Service Certificate are released by the HR Assistant after all approvals, the Competent Authority and the No-Dues (including Establishment) are complete."
              />
            )}
            <div className="row g-3">
              {[
                { docType: "acceptance_letter" as const, docName: "Acceptance Letter", available: caApproved },
                { docType: "service_certificate" as const, docName: "Service Certificate", available: finalReleased },
                { docType: "relieving_letter" as const, docName: "Relieving Order", available: finalReleased },
                { docType: "no_dues_certificate_doc" as const, docName: "No Dues Certificate", available: ndSubmitted },
              ].map((doc) => (
                <div className="col-md-3 col-sm-6" key={doc.docType}>
                  <button
                    type="button"
                    className="card border-0 shadow-sm w-100 h-100 text-center p-3"
                    style={{ borderRadius: 14, opacity: doc.available ? 1 : 0.6, cursor: "pointer" }}
                    onClick={() => handleDownloadDoc(doc.docType, doc.docName, doc.available)}
                  >
                    <span
                      className="d-inline-flex align-items-center justify-content-center mx-auto mb-2"
                      style={{
                        width: 44, height: 44, borderRadius: 12,
                        background: doc.available ? "#E6F1FB" : "rgba(0,0,0,0.05)",
                        color: doc.available ? "#185FA5" : "#9aa0a6",
                      }}
                    >
                      {downloading[doc.docType] ? (
                        <span className="spinner-border spinner-border-sm" role="status" />
                      ) : (
                        <i className="ti ti-file-download" style={{ fontSize: 20 }} />
                      )}
                    </span>
                    <div className="fw-semibold small">{doc.docName}</div>
                    {doc.available ? (
                      <span
                        className="d-inline-flex align-items-center gap-1 mt-2 px-2 py-1 rounded-pill mx-auto"
                        style={{ background: "#E6F1FB", color: "#185FA5", fontSize: 11 }}
                      >
                        <i className="ti ti-download" /> PDF
                      </span>
                    ) : (
                      <span className="text-muted mt-2 d-block" style={{ fontSize: 11 }}>
                        Pending
                      </span>
                    )}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <DeptClearanceDetailModal
        open={detailOpen}
        deptName={detailDept}
        loading={detailLoading}
        items={detailItems}
        onClose={() => setDetailOpen(false)}
      />
    </div>
  );
};

export default NoDuesHRReviewDetail;
